import { useEffect, useRef, useState } from 'react';
import { defineMaterial, type MaterialComponentProps } from '@h5/schema';
import { TextPropsSchema, type TextProps } from './schema';

export { TextPropsSchema, type TextProps };

/** 文本物料：编辑态双击进入行内编辑，blur 时回写 content。 */
function TextView({ props, ctx }: MaterialComponentProps<TextProps>) {
  const editable = ctx.mode === 'edit' && typeof ctx.updateProps === 'function';
  const [editing, setEditing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // 进入编辑时聚焦并把光标放到末尾
  useEffect(() => {
    if (!editing || !ref.current) return;
    const el = ref.current;
    el.focus();
    const sel = el.ownerDocument.getSelection();
    if (sel) {
      const range = el.ownerDocument.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, [editing]);

  const commit = () => {
    if (!ref.current) return;
    const next = ref.current.innerText;
    setEditing(false);
    if (next !== props.content) ctx.updateProps?.({ content: next });
  };

  return (
    <div
      ref={ref}
      contentEditable={editing}
      suppressContentEditableWarning
      onDoubleClick={editable ? () => setEditing(true) : undefined}
      onBlur={editing ? commit : undefined}
      onKeyDown={editing ? (e) => { if (e.key === 'Escape') commit(); } : undefined}
      style={{
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        fontSize: props.fontSize,
        color: props.color,
        fontWeight: props.fontWeight,
        textAlign: props.align,
        lineHeight: props.lineHeight,
        letterSpacing: props.letterSpacing,
        fontFamily: props.fontFamily || undefined,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        outline: editing ? '1px dashed #1677ff' : 'none',
        cursor: editing ? 'text' : undefined,
        userSelect: editing ? 'text' : 'none',
      }}
    >
      {props.content}
    </div>
  );
}

export const textMaterial = defineMaterial<TextProps>({
  id: 'text',
  version: 1,
  name: '文本',
  category: 'basic',
  icon: 'T',
  defaultSize: { w: 200, h: 40 },
  defaultProps: TextPropsSchema.parse({}),
  propsSchema: TextPropsSchema,
  capabilities: { inlineEdit: true },
  component: TextView,
});
